import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { useForm, Controller } from 'react-hook-form';
import ProfessionalRichTextEditor from '../components/ProfessionalRichTextEditor';
import {
  ArrowLeft,
  DollarSign,
  Clock,
  MapPin,
  Tag,
  FileText,
  Users,
  Calendar
} from 'lucide-react';

const EditJobPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch job details
  const { data: job, isLoading, error } = useQuery(
    ['job', id],
    () => api.get(API_ENDPOINTS.JOBS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  const { register, handleSubmit, formState: { errors }, setValue, watch, control } = useForm();

  // State for image upload
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Update job mutation with FormData support
  const updateJobMutation = useMutation(
    (formData) => {
      return api.put(API_ENDPOINTS.JOBS.UPDATE(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: () => {
        toast.success('Job updated successfully!');
        queryClient.invalidateQueries(['job', id]);
        navigate(`/jobs/${id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update job');
      }
    }
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Populate form when job data is loaded
  useEffect(() => {
    if (job) {
      setValue('title', job.title);
      setValue('description', job.description);
      setValue('category', job.category);
      setValue('type', job.type);
      setValue('budget.fixed', job.budget?.fixed);
      setValue('budget.min', job.budget?.min);
      setValue('budget.max', job.budget?.max);
      setValue('duration.value', job.duration?.value);
      setValue('duration.unit', job.duration?.unit);
      setValue('location.city', job.location?.city);
      setValue('location.country', job.location?.country);
      setValue('location.remote', job.location?.remote);
      setValue('skills', job.skills?.join(', '));
      setValue('requirements', job.requirements?.join(', '));
      setValue('deliverables', job.deliverables?.join(', '));
      
      // Set image preview from existing imageUrl
      if (job.imageUrl) {
        setImagePreview(job.imageUrl);
      }
    }
  }, [job, setValue]);

  const onSubmit = (data) => {
    // Build budget object based on budget type
    let budget = {};
    if (budgetType === 'range') {
      if (data.budget?.min && data.budget?.max) {
        budget = {
          min: parseFloat(data.budget.min),
          max: parseFloat(data.budget.max)
        };
      }
    } else if (data.budget?.fixed) {
      budget = {
        fixed: parseFloat(data.budget.fixed)
      };
    }

    // Create FormData for optional file upload
    const formData = new FormData();
    
    // Append all text fields
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    formData.append('type', data.type);
    formData.append('budget', JSON.stringify(budget));
    
    // Append duration
    if (data.duration?.value && data.duration?.unit) {
      formData.append('duration', JSON.stringify({
        value: parseInt(data.duration.value),
        unit: data.duration.unit
      }));
    }
    
    // Append arrays
    formData.append('requirements', JSON.stringify(
      data.requirements ? data.requirements.split(',').map(req => req.trim()).filter(req => req) : []
    ));
    formData.append('deliverables', JSON.stringify(
      data.deliverables ? data.deliverables.split(',').map(del => del.trim()).filter(del => del) : []
    ));
    formData.append('skills', JSON.stringify(
      data.skills ? data.skills.split(',').map(skill => skill.trim()).filter(skill => skill) : []
    ));

    // Append image file only if new image was selected
    if (selectedImage) {
      formData.append('image', selectedImage);
    }

    updateJobMutation.mutate(formData);
  };

  const budgetType = watch('budgetType') || (job?.budget?.fixed ? 'fixed' : 'range');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Job not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The job you're trying to edit doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="btn-primary"
          >
            Browse Jobs
          </button>
        </div>
      </div>
    );
  }

  // Check if user is the job owner
  if (job.client._id !== user?._id) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You can only edit jobs that you created.
          </p>
          <button
            onClick={() => navigate(`/jobs/${id}`)}
            className="btn-primary"
          >
            View Job
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate(`/jobs/${id}`)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading">
              Edit Job
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Update your job posting details
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Title *
                </label>
                <input
                  {...register('title', { required: 'Job title is required' })}
                  type="text"
                  className="w-full input-field"
                  placeholder="e.g., Senior React Developer"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Description *
                </label>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Job description is required' }}
                    render={({ field }) => (
                    <ProfessionalRichTextEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Describe the role, responsibilities, and what you're looking for. You can format text, add headings, links, images, videos, and tables..."
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  {...register('category', { required: 'Category is required' })}
                  className="w-full input-field"
                >
                  <option value="">Select a category</option>
                  <option value="web-development">Web Development</option>
                  <option value="mobile-development">Mobile Development</option>
                  <option value="design">Design</option>
                  <option value="writing">Writing</option>
                  <option value="marketing">Marketing</option>
                  <option value="data-science">Data Science</option>
                  <option value="other">Other</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Type *
                </label>
                <select
                  {...register('type', { required: 'Job type is required' })}
                  className="w-full input-field"
                >
                  <option value="">Select job type</option>
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="freelance">Freelance</option>
                  <option value="contract">Contract</option>
                </select>
                {errors.type && (
                  <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                )}
              </div>

              {/* Job Image Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Image {job?.imageUrl ? '(Optional - leave empty to keep current)' : '(Optional)'}
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark"
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  )}
                  {!imagePreview && job?.imageUrl && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Current image will be kept if no new image is uploaded
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Budget */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Budget
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Budget Type
                </label>
                <select
                  {...register('budgetType')}
                  className="w-full input-field"
                >
                  <option value="fixed">Fixed Amount</option>
                  <option value="range">Range</option>
                </select>
              </div>

              {budgetType === 'fixed' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fixed Amount ($)
                  </label>
                  <input
                    {...register('budget.fixed', { 
                      valueAsNumber: true,
                      min: { value: 1, message: 'Amount must be at least 1' },
                      validate: (value) => !value || Number.isInteger(Number(value)) || 'Amount must be a whole number'
                    })}
                    type="number"
                    min="1"
                    className="w-full input-field"
                    placeholder="e.g., 5000"
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow positive integers
                      if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                        const { onChange } = register('budget.fixed');
                        onChange(e);
                      }
                    }}
                  />
                  {errors.budget?.fixed && (
                    <p className="text-red-500 text-sm mt-1">{errors.budget.fixed.message}</p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Minimum Budget ($)
                    </label>
                    <input
                      {...register('budget.min', { 
                        valueAsNumber: true,
                        min: { value: 1, message: 'Amount must be at least 1' },
                        validate: (value) => !value || Number.isInteger(Number(value)) || 'Amount must be a whole number'
                      })}
                      type="number"
                      min="1"
                      className="w-full input-field"
                      placeholder="e.g., 3000"
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          const { onChange } = register('budget.min');
                          onChange(e);
                        }
                      }}
                    />
                    {errors.budget?.min && (
                      <p className="text-red-500 text-sm mt-1">{errors.budget.min.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum Budget ($)
                    </label>
                    <input
                      {...register('budget.max', { 
                        valueAsNumber: true,
                        min: { value: 1, message: 'Amount must be at least 1' },
                        validate: (value) => !value || Number.isInteger(Number(value)) || 'Amount must be a whole number'
                      })}
                      type="number"
                      min="1"
                      className="w-full input-field"
                      placeholder="e.g., 8000"
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          const { onChange } = register('budget.max');
                          onChange(e);
                        }
                      }}
                    />
                    {errors.budget?.max && (
                      <p className="text-red-500 text-sm mt-1">{errors.budget.max.message}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Duration
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration Value
                </label>
                <input
                  {...register('duration.value', { 
                    valueAsNumber: true,
                    min: { value: 1, message: 'Duration must be at least 1' },
                    validate: (value) => !value || Number.isInteger(Number(value)) || 'Duration must be a whole number'
                  })}
                  type="number"
                  min="1"
                  className="w-full input-field"
                  placeholder="e.g., 3"
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                      const { onChange } = register('duration.value');
                      onChange(e);
                    }
                  }}
                />
                {errors.duration?.value && (
                  <p className="text-red-500 text-sm mt-1">{errors.duration.value.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration Unit
                </label>
                <select
                  {...register('duration.unit')}
                  className="w-full input-field"
                >
                  <option value="">Select unit</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Location
            </h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  {...register('location.remote')}
                  type="checkbox"
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Remote work available
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    {...register('location.city')}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., New York"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Country
                  </label>
                  <input
                    {...register('location.country')}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., United States"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Skills and Requirements */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Skills and Requirements
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Required Skills (comma-separated)
                </label>
                <input
                  {...register('skills')}
                  type="text"
                  className="w-full input-field"
                  placeholder="e.g., React, Node.js, MongoDB"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Requirements (comma-separated)
                </label>
                <input
                  {...register('requirements')}
                  type="text"
                  className="w-full input-field"
                  placeholder="e.g., 3+ years experience, Portfolio required"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Deliverables (comma-separated)
                </label>
                <input
                  {...register('deliverables')}
                  type="text"
                  className="w-full input-field"
                  placeholder="e.g., Working application, Documentation, Code review"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/jobs/${id}`)}
              className="btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateJobMutation.isLoading}
              className="btn-primary"
            >
              {updateJobMutation.isLoading ? 'Updating...' : 'Update Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditJobPage;
